import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { formatLong } from "./formatters";

const TeamDeleteBookingDialog = ({
  open,
  booking,
  subject,
  dateLocale,
  t,
  busy,
  variant = "cancel",
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const isPermanent = variant === "permanent";
  const isCustomer = variant === "customer";

  useEffect(() => {
    if (open) setReason("");
  }, [open, booking?._id, variant]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open || (!booking && !subject)) return null;

  const label =
    subject?.name ||
    `${booking.customer?.firstName || ""} ${booking.customer?.lastName || ""}`.trim();
  const when = booking
    ? formatLong(booking.start, dateLocale)
    : subject?.subtitle || "";
  const canConfirm =
    (isPermanent || isCustomer || reason.trim().length > 0) && !busy;

  const titleKey = isCustomer
    ? "customers.permanentDeleteTitle"
    : isPermanent
      ? "bookings.permanentDeleteTitle"
      : "bookings.deleteTitle";
  const confirmKey = isCustomer
    ? "customers.permanentDeleteConfirm"
    : isPermanent
      ? "bookings.permanentDeleteConfirm"
      : "bookings.deleteConfirm";
  const reasonLabelKey = isCustomer
    ? "customers.permanentDeleteReasonLabel"
    : isPermanent
      ? "bookings.permanentDeleteReasonLabel"
      : "bookings.deleteReasonLabel";
  const reasonHintKey = isCustomer
    ? "customers.permanentDeleteReasonHint"
    : isPermanent
      ? "bookings.permanentDeleteReasonHint"
      : "bookings.deleteReasonHint";
  const reasonPlaceholderKey = isCustomer
    ? "customers.permanentDeleteReasonPlaceholder"
    : isPermanent
      ? "bookings.permanentDeleteReasonPlaceholder"
      : "bookings.deleteReasonPlaceholder";
  const confirmBtnKey = isCustomer
    ? "customers.permanentDeleteConfirmBtn"
    : isPermanent
      ? "bookings.permanentDeleteConfirmBtn"
      : "bookings.deleteConfirmBtn";

  return createPortal(
    <div className="team-delete-dialog-root">
      <div
        className="team-delete-dialog-overlay"
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="team-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="team-delete-dialog-title"
      >
        <h2 id="team-delete-dialog-title">{t(titleKey)}</h2>
        <p className="team-delete-dialog-lead">
          {t(confirmKey, {
            name: label || booking?.customer?.email || subject?.subtitle || "—",
            when: when || "—",
          })}
        </p>
        <label className="team-delete-reason-label" htmlFor="team-delete-reason">
          {t(reasonLabelKey)}
        </label>
        <p className="team-delete-reason-hint">{t(reasonHintKey)}</p>
        <textarea
          id="team-delete-reason"
          className="team-delete-reason-input"
          rows={3}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder={t(reasonPlaceholderKey)}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="team-delete-dialog-actions">
          <button type="button" className="team-dialog-cancel" onClick={onClose} disabled={busy}>
            {t("common.close")}
          </button>
          <button
            type="button"
            className="team-delete-btn team-delete-btn--confirm"
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? t("bookings.deleteBusy") : t(confirmBtnKey)}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamDeleteBookingDialog;
